import * as React from 'react'
import { HelpCircle } from 'lucide-react'

import type { LeadQualificationFunnel, OpeningTemplateEffectiveness } from '@sdr-agend/admin-schemas'
import { formatPercentOrDash } from '@/lib/format'
import { formatMinutes } from '@/components/dashboard/LeadQualificationFunnel'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * "Performance da IA" (2026-07-31, user request — redesign visual pra bater
 * com a referência "PAINEL COMERCIAL — IA SDR") — painel compacto reunindo
 * indicadores que já existem espalhados em outras seções desta mesma
 * página (`openingTemplateEffectiveness`/`leadQualificationFunnel`),
 * SEM nenhuma métrica nova. Puramente uma segunda visão/agrupamento do
 * mesmo dado já calculado pela rota.
 */
export interface AiPerformancePanelProps {
  openingTemplateEffectiveness: OpeningTemplateEffectiveness
  leadQualificationFunnel: LeadQualificationFunnel
  /** `commercialHealthIndex.qualificationRate` (0-100, já clampado) — evita recalcular a mesma taxa duas vezes. */
  qualificationRate: number | null
  /**
   * 2026-08-17 (user request via chamado — Haislann) — os tempos médios (1ª
   * resposta/qualificação/agendamento) saíram do painel principal por serem
   * operacionais demais para a visão executiva. Continuam disponíveis aqui
   * pra quem reaproveitar este painel num dashboard dedicado de IA/SDR.
   * Default `true` preserva o comportamento anterior para quem já consome
   * este componente sem passar a prop.
   */
  showTimingRows?: boolean
}

interface Row {
  key: string
  label: string
  value: string
  /**
   * 2026-08-11 (user request) — algumas taxas desta lista reaproveitam o
   * mesmo "conceito" de outro card na página (ex.: "Qualificados" do Funil
   * Comercial), mas com uma base de cálculo diferente. Sem essa explicação,
   * os dois números parecem contraditórios em vez de duas leituras válidas.
   */
  tooltip?: string
}

export function AiPerformancePanel({
  openingTemplateEffectiveness: ote,
  leadQualificationFunnel: funnel,
  qualificationRate,
  showTimingRows = true,
}: AiPerformancePanelProps): React.ReactElement {
  const rateRows: Row[] = [
    { key: 'contacted', label: 'Leads abordados', value: ote.messagesSent.toLocaleString('pt-BR') },
    { key: 'responseRate', label: 'Taxa de resposta', value: formatPercentOrDash(ote.responseRate) },
    {
      key: 'qualificationRate',
      label: 'Taxa de qualificação',
      value: qualificationRate === null ? 'N/A' : `${Math.round(qualificationRate)}%`,
      tooltip:
        'Qualificados ÷ todos os leads recebidos (inclui quem nunca respondeu). Diferente de "Qualificados" no Funil Comercial, que é sobre quem respondeu — por isso os dois números não batem.',
    },
    { key: 'schedulingRate', label: 'Taxa de agendamento', value: formatPercentOrDash(ote.schedulingRate) },
  ]

  const timingRows: Row[] = [
    { key: 'firstResponse', label: 'Tempo médio até 1ª resposta', value: formatMinutes(funnel.avgMinutesToFirstResponse) },
    { key: 'qualified', label: 'Tempo médio até qualificação', value: formatMinutes(funnel.avgMinutesToQualified) },
    { key: 'scheduled', label: 'Tempo médio até agendamento', value: formatMinutes(funnel.avgMinutesToScheduled) },
  ]

  return (
    <div className="grid gap-3" data-testid="ai-performance-panel">
      <div className="grid gap-1.5">
        {rateRows.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between gap-2 text-xs"
            data-testid={`ai-performance-${row.key}`}
          >
            <span className="flex items-center gap-1 text-muted-foreground">
              {row.label}
              {row.tooltip !== undefined && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="shrink-0 text-muted-foreground/70 hover:text-foreground"
                      aria-label={`O que é "${row.label}"?`}
                    >
                      <HelpCircle className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{row.tooltip}</TooltipContent>
                </Tooltip>
              )}
            </span>
            <span className="font-medium tabular-nums">{row.value}</span>
          </div>
        ))}
      </div>
      {showTimingRows && (
        <div className="grid gap-1.5 border-t pt-3">
          {timingRows.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-2 text-xs"
              data-testid={`ai-performance-${row.key}`}
            >
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-medium tabular-nums">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AiPerformancePanel
